import {gql} from '@apollo/client';

import {Comment, CommentWithImages, Like, Image} from 'web-app/api/shared-fragments';

export const AgeBandSetting = gql`
    fragment AgeBandSetting on AgeBandSetting {
        ageBandSettingId
        id: ageBandSettingId ## shorthand ID for convenience
        assessmentSettingsId
        from
        to
        label
    }
`;

export const AssessmentOptionSetting = gql`
    fragment AssessmentOptionSetting on AssessmentOptionSetting {
        assessmentOptionSettingId
        id: assessmentOptionSettingId ## shorthand ID for convenience
        assessmentSettingsId
        backgroundColor
        fontColor
        label
    }
`;

export const MinimalFramework = gql`
    fragment MinimalFramework on Framework {
        id
        title
        abbr
        areas {
            title
            description
            abbr
            color
            id
        }
    }
`;

export const BehaviorCanLinkToFrameworks = gql`
    fragment BehaviorCanLinkToFrameworks on BehaviorCanLinkToFrameworks {
        id: behaviorId
        __typename
        frameworks {
            ...MinimalFramework
        }
    }
    ${MinimalFramework}
`;

export const ObservationDataWithNoComments = gql`
    fragment ObservationDataWithNoComments on Observation {
        children {
            id
            name
            institutionId
            # Used to update cache correctly in Famly Home
            profileImage {
                url
            }
        }
        id
        version
        feedItem {
            id
        }
        createdBy {
            name {
                fullName
            }
            profileImage {
                url
            }
        }
        status {
            state
            createdAt
        }
        variant
        settings {
            assessmentSetting {
                assessmentSettingsId
                title
            }
        }
        behaviors {
            id: behaviorId
            ... on BehaviorCanLinkToFrameworks {
                ...BehaviorCanLinkToFrameworks
            }
            ... on BehaviorObservationVariantAmbiguity {
                variants
            }
        }
        remark {
            id
            body
            richTextBody
            date
            statements {
                refinement
                statement {
                    body
                    id
                    area {
                        frameworkId
                        id
                        lower
                        upper
                        title
                        abbr
                        color
                        deletedAt
                        subAreas {
                            title
                        }
                    }
                }
            }
            areas {
                area {
                    frameworkId
                    id
                    parentId
                    title
                    description
                    abbr
                    color
                    placement
                    deletedAt
                    framework {
                        id
                        title
                        owner
                    }
                }
                refinement
                note
                areaRefinementSettings {
                    ageBandSetting {
                        ...AgeBandSetting
                    }
                    assessmentOptionSetting {
                        ...AssessmentOptionSetting
                    }
                }
            }
            customFieldValues {
                customFieldSetting {
                    assessmentSettingsId
                    customFieldId
                    label
                    order
                }
                value
            }
        }
        nextStep {
            id
            body
            richTextBody
        }
        files {
            name
            url
            id
        }
        images {
            height
            width
            id
            secret {
                crop
                expires
                key
                path
                prefix
            }
        }
        videos {
            ... on TranscodingVideo {
                id
            }
            ... on TranscodedVideo {
                duration
                height
                id
                thumbnailUrl
                videoUrl
                width
            }
        }
        likes {
            count
            likedByMe
            likes {
                ...Like
            }
        }
        canReport
    }
    ${Like}
    ${BehaviorCanLinkToFrameworks}
    ${AgeBandSetting}
    ${AssessmentOptionSetting}
`;

export const ObservationData = gql`
    fragment ObservationData on Observation {
        ...ObservationDataWithNoComments
        comments {
            count
            results {
                ...Comment
            }
        }
    }
    ${Comment}
    ${ObservationDataWithNoComments}
`;

export const ObservationDataWithCommentsImages = gql`
    fragment ObservationDataWithCommentsImages on Observation {
        ...ObservationDataWithNoComments
        comments {
            count
            results {
                ...CommentWithImages
            }
        }
    }
    ${Image}
    ${CommentWithImages}
    ${ObservationDataWithNoComments}
`;

export const Area = gql`
    fragment Area on Area {
        title
        description
        abbr
        color
        id
        lower
        upper
        framework {
            id
            title
            abbr
        }
        statements {
            id
            body
        }
    }
`;

export const Framework = gql`
    fragment Framework on Framework {
        id
        title
        abbr
        areas {
            ...Area
            subAreas {
                ...Area
                subAreas {
                    ...Area
                    subAreas {
                        ...Area
                    }
                }
            }
        }
    }
    ${Area}
`;

export const RemarkInfo = gql`
    fragment RemarkInfo on ChildDevelopmentObservationRemark {
        __typename
        body
        richTextBody
        date
        id
        status
    }
`;

export const NextStepData = gql`
    fragment NextStepData on Observation {
        id
        children {
            id
            child {
                institutionId
                id
                group {
                    title
                    id
                }
                name {
                    fullName
                }
                profileImage {
                    url
                }
                birthday
            }
        }
        feedItem {
            id
        }
        status {
            createdAt
        }
        nextStep {
            ...RemarkInfo
        }
        remark {
            ...RemarkInfo
        }
    }
    ${RemarkInfo}
`;

export const ObservationListData = gql`
    fragment ObservationListData on Observation {
        feedItem {
            id
        }
        id
        version
        children {
            id
            name
            child {
                id
                group {
                    title
                    id
                }
                name {
                    fullName
                }
                profileImage {
                    url
                }
                birthday
            }
        }
        createdBy {
            name {
                fullName
            }
            profileImage {
                url
            }
        }
        status {
            state
            createdAt
        }
        statuses {
            createdAt
            state
        }
        variant
        behaviors {
            id: behaviorId
            ... on BehaviorCanLinkToFrameworks {
                frameworks {
                    id
                    title
                }
            }
        }
        remark {
            id
            body
            richTextBody
            date
            statements {
                refinement
                statement {
                    body
                    id
                    area {
                        id
                        lower
                        upper
                        title
                        abbr
                        color
                        deletedAt
                        subAreas {
                            title
                        }
                        framework {
                            title
                            id
                        }
                        parent {
                            color
                            abbr
                            id
                            title
                            framework {
                                title
                                id
                            }
                            parent {
                                color
                                abbr
                                id
                                title
                                framework {
                                    title
                                    id
                                }
                                parent {
                                    color
                                    abbr
                                    id
                                    title
                                    framework {
                                        title
                                        id
                                    }
                                }
                            }
                        }
                    }
                }
            }
            areas {
                area {
                    id
                    color
                    abbr
                    title
                    deletedAt
                    framework {
                        id
                        title
                    }
                }
                refinement
            }
        }
        files {
            name
            url
            id
        }
        images {
            height
            width
            id
            secret {
                expires
                key
                path
                prefix
            }
        }
        videos {
            ... on TranscodingVideo {
                id
            }
            ... on TranscodedVideo {
                duration
                height
                id
                thumbnailUrl
                videoUrl
                width
            }
        }
    }
`;
